import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogTrigger,
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
  Textarea,
} from "@egaranti/components";
import { useToast } from "@egaranti/components";
import { FormDescription } from "@egaranti/components";
import { zodResolver } from "@hookform/resolvers/zod";

import { useEffect, useRef, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { FileText, Plus, Trash2, Upload, X } from "lucide-react";

import ProductService from "@/services/productService";

import useExtendedWarrantyStore from "@/stores/useExtendedWarranty";

import CollectionTypesField, {
  DEFAULT_COLLECTION_TYPES,
  normalizeCollectionTypes,
} from "@/components/extendedWarranty/packages/collectionTypesField";
import ImageBlock from "@/components/shared/ImageBlock";
import { Breadcrumb, BreadcrumbItem } from "@/components/shared/breadcrumbs";
import { InputWithIcon } from "@/components/ui/inputWithIcon";

import * as z from "zod";

import {
  hasFilledBarems,
  hasFilledRateBarems,
  validateBarems,
} from "./validateBarems";

const PackageDetail = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const {
    currentPlan,
    loading,
    getAdditionalServicePlan,
    updateAdditionalServicePlan,
    deleteAdditionalServicePlan,
  } = useExtendedWarrantyStore();

  // EPS-4856 (B-2/B-3): Türkçe error messages + makul üst limitler.
  const MAX_TL = 9_999_999;
  const moneyField = () =>
    z
      .number()
      .min(0, { message: t("extendedWarranties.validateMsg.negativeAmount", "Fiyat negatif olamaz") })
      .max(MAX_TL, {
        message: t(
          "extendedWarranties.validateMsg.amountTooLarge",
          "Fiyat 9.999.999 TL'yi aşamaz",
        ),
      })
      .optional();
  const rateField = () =>
    z
      .number()
      .min(0, { message: t("extendedWarranties.validateMsg.negativeRate", "Oran negatif olamaz") })
      .max(100, {
        message: t("extendedWarranties.validateMsg.rateTooLarge", "Oran %100'den büyük olamaz"),
      })
      .optional();

  // EPS-4855: BAREM çoklu fiyat aralığı (barems[]).
  const baremItemSchema = z.object({
    id: z.number().int().optional(), // BE PUT: id stabil tutmak için
    minPrice: moneyField(),
    maxPrice: moneyField(),
    planPrice: moneyField(),
    packageCode: z.string().optional().or(z.literal("")),
  });

  // EPS-4970: Standart Planın oransal aralığı — düz fiyat yerine oran.
  const rateBaremItemSchema = z.object({
    id: z.number().int().optional(),
    minPrice: moneyField(),
    maxPrice: moneyField(),
    rate: rateField(),
    packageCode: z.string().optional().or(z.literal("")),
  });

  const formSchema = z.object({
    name: z.string().optional().or(z.literal("")),
    // EPS-4863: ay bazlı süre. String olarak tutulur; payload'da parseInt
    // ile sayıya çevrilir. 0 = bakım paketi, 0-72 ay aralığı.
    durationMonth: z
      .string()
      .min(
        1,
        t(
          "extendedWarranties.validateMsg.durationMonthRequired",
          "Süre (ay) zorunludur",
        ),
      )
      .regex(
        /^\d+$/,
        t(
          "extendedWarranties.validateMsg.durationMonthNatural",
          "Sadece doğal sayı girilebilir",
        ),
      )
      .refine(
        (v) => {
          const n = parseInt(v, 10);
          return n >= 0 && n <= 72;
        },
        t(
          "extendedWarranties.validateMsg.durationMonthRange",
          "Süre 0 ile 72 ay arasında olmalıdır",
        ),
      ),
    // EPS-4970 revize: satış fiyatı ve komisyon oranları formdan kaldırıldı —
    // Standart Plan fiyatı yalnızca oransal aralıktan hesaplanıyor. Alanlar
    // BE'de duruyor; bu ekran artık ne gösteriyor ne de gönderiyor, mevcut
    // değerler MapStruct partialUpdate (null = dokunma) ile korunuyor.
    status: z.string().nullable(),
    // EPS-4930: baremli plan paket kategorisi — BAREM'de zorunlu, onSubmit'te
    // kontrol edilir (schema permissive; BASIC planda alan yok).
    packageCategory: z.enum(["EXTENDED_WARRANTY", "OTHER"]).optional(),
    barems: z.array(baremItemSchema).optional(),
    // EPS-4970: Standart Planın oransal barem aralıkları.
    rateBarems: z.array(rateBaremItemSchema).optional(),
    // EPS-4971: Tahsilat yöntemi — en az bir yöntem zorunlu, plan oluşturmadaki
    // kuralın aynısı. Plan oluşturulduktan sonra değiştirilebilir (A3).
    collectionTypes: z
      .array(z.enum(["SELLER", "ONLINE"]))
      .min(
        1,
        t(
          "extendedWarranties.validateMsg.collectionTypeRequired",
          "En az bir tahsilat yöntemi seçilmelidir",
        ),
      ),
    // EPS-5860: embed-app AddonCard'da gösterilir.
    planDescription: z.string().max(2000).optional().or(z.literal("")),
  });

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      durationMonth: "12",
      status: null,
      packageCategory: undefined,
      barems: [
        {
          minPrice: undefined,
          maxPrice: undefined,
          planPrice: undefined,
          packageCode: "",
        },
      ],
      rateBarems: [],
      planDescription: "",
      collectionTypes: DEFAULT_COLLECTION_TYPES,
    },
  });

  const baremsArray = useFieldArray({
    control: form.control,
    name: "barems",
  });

  // EPS-4970: Standart Planın oransal aralıkları
  const rateBaremsArray = useFieldArray({
    control: form.control,
    name: "rateBarems",
  });

  const [contractPdfFile, setContractPdfFile] = useState(null);
  const pdfInputRef = useRef(null);

  const isBarem = currentPlan?.planType === "BAREM";
  const [mainCategoryName, setMainCategoryName] = useState("");
  const [categoryName, setCategoryName] = useState("");
  const [subCategoryName, setSubCategoryName] = useState("");
  const [subCategoryBrandName, setSubCategoryBrandName] = useState("");
  const [merchantProductName, setMerchantProductName] = useState("");

  // 3-level image management on the BAREM edit page (mirrors newPackagePage).
  // Categories/product are read-only metadata (id is captured at create-time)
  // but the merchant should still be able to view, replace, or remove the
  // images that belong to those entities — same UX as the product edit page.
  const [mainCategoryImageUrl, setMainCategoryImageUrl] = useState("");
  const [categoryImageUrl, setCategoryImageUrl] = useState("");
  const [productImageUrl, setProductImageUrl] = useState("");
  const [categorySerialNoInfo, setCategorySerialNoInfo] = useState("");
  const [serialNoInfoBusy, setSerialNoInfoBusy] = useState(false);
  const [imageBusy, setImageBusy] = useState({
    main: false,
    cat: false,
    product: false,
  });
  const mainImgRef = useRef(null);
  const catImgRef = useRef(null);
  const productImgRef = useRef(null);

  useEffect(() => {
    const loadPlan = async () => {
      try {
        const data = await getAdditionalServicePlan(id);

        // EPS-4855: BE response artık barems array döner. Geriye uyumluluk:
        // eski API'lerden tek-satır gelirse barems boş olur → root alanlardan
        // sentetik bir barems satırı oluştur.
        const baremsFromBE = Array.isArray(data.barems) ? data.barems : [];
        // EPS-4963: Baremler her zaman minimum fiyata göre artan sırada
        // gösterilmeli. BE siblings'ı id (ekleme) sırasında döndüğünden
        // FE'de minPrice ASC sıralıyoruz; böylece kaydetme/yenileme sonrası
        // da sıra korunur. null minPrice en sona düşer.
        const baremsSorted = [...baremsFromBE].sort((a, b) => {
          const av = a.minPrice ?? Number.POSITIVE_INFINITY;
          const bv = b.minPrice ?? Number.POSITIVE_INFINITY;
          return av - bv;
        });
        const baremsForForm =
          baremsSorted.length > 0
            ? baremsSorted.map((b) => ({
                id: b.id,
                minPrice: b.minPrice,
                maxPrice: b.maxPrice,
                planPrice: b.planPrice,
                packageCode: b.packageCode || "",
              }))
            : [
                {
                  minPrice: data.minPrice,
                  maxPrice: data.maxPrice,
                  planPrice: data.planPrice,
                  packageCode: data.packageCode || "",
                },
              ];

        form.reset({
          name: data.name || "",
          // EPS-4863: durationMonth string olarak tutulur (input type=text).
          // BE'den number gelir; String() ile çevrilir. Eski planlarda
          // warrantyYear*12 (ya da duration*12) fallback.
          durationMonth: String(
            data.durationMonth ??
              (data.warrantyYear ?? data.duration ?? 1) * 12,
          ),
          status: data.status,
          // EPS-4930: migration mevcut BAREM planlarını EXTENDED_WARRANTY'ye
          // backfill eder; yine de BE null dönerse alan boş gelir ve kayıt
          // öncesi seçim istenir.
          packageCategory: data.packageCategory || undefined,
          barems: baremsForForm,
          // EPS-4970: Standart Planın oransal aralıkları. BE aralıkları minPrice
          // ASC döndürüyor; yine de defansif sıralıyoruz.
          rateBarems: [...(Array.isArray(data.rateBarems) ? data.rateBarems : [])]
            .sort(
              (a, b) =>
                (a.minPrice ?? Number.POSITIVE_INFINITY) -
                (b.minPrice ?? Number.POSITIVE_INFINITY),
            )
            .map((b) => ({
              id: b.id,
              minPrice: b.minPrice,
              maxPrice: b.maxPrice,
              rate: b.rate,
              packageCode: b.packageCode || "",
            })),
          planDescription: data.planDescription || "",
          // EPS-4971: eski planlarda alan boş olabilir → bayi tahsilatı.
          collectionTypes: normalizeCollectionTypes(data.collectionTypes),
        });

        // EPS-4970: Standart Plan da kategori hiyerarşisine bağlanabildiği için
        // ad/görsel çözümlemesi artık plan tipinden bağımsız; ilgili id yoksa
        // bloklar zaten atlanıyor.
        {
          if (data.mainCategoryId) {
            try {
              const mc = await ProductService.getMainCategories();
              const mainList = mc?.data || [];
              const main = mainList.find(
                (c) => Number(c.id) === Number(data.mainCategoryId),
              );
              setMainCategoryName(main?.name || `#${data.mainCategoryId}`);
              setMainCategoryImageUrl(main?.imageUrl || "");
            } catch {
              setMainCategoryName(`#${data.mainCategoryId}`);
            }
          }
          if (data.categoryId && data.mainCategoryId) {
            try {
              const c = await ProductService.getCategories(data.mainCategoryId);
              const catList = c?.data || [];
              const cat = catList.find(
                (x) => Number(x.id) === Number(data.categoryId),
              );
              setCategoryName(cat?.name || `#${data.categoryId}`);
              setCategoryImageUrl(cat?.imageUrl || "");
              setCategorySerialNoInfo(cat?.serialNoInfo || "");
            } catch {
              setCategoryName(`#${data.categoryId}`);
            }
          }
          // EPS-4873: alt kategori + marka isimlerini görüntüleme amacıyla resolve et.
          if (data.subCategoryId && data.categoryId) {
            try {
              const s = await ProductService.getSubCategories(data.categoryId);
              const subList = s?.data || [];
              const sub = subList.find(
                (x) => Number(x.id) === Number(data.subCategoryId),
              );
              setSubCategoryName(sub?.name || `#${data.subCategoryId}`);
            } catch {
              setSubCategoryName(`#${data.subCategoryId}`);
            }
          }
          if (data.subCategoryBrandId && data.subCategoryId) {
            try {
              const b = await ProductService.getSubCategoryBrands(
                data.subCategoryId,
              );
              const brandList = b?.data || [];
              const brand = brandList.find(
                (x) => Number(x.id) === Number(data.subCategoryBrandId),
              );
              setSubCategoryBrandName(
                brand?.name || `#${data.subCategoryBrandId}`,
              );
            } catch {
              setSubCategoryBrandName(`#${data.subCategoryBrandId}`);
            }
          }
          if (data.merchantProductId) {
            try {
              const p = await ProductService.getProduct(data.merchantProductId);
              setMerchantProductName(
                p?.data?.name || p?.name || `#${data.merchantProductId}`,
              );
              setProductImageUrl(p?.data?.imageUrl || p?.imageUrl || "");
            } catch {
              setMerchantProductName(`#${data.merchantProductId}`);
            }
          }
        }
      } catch (error) {
        // EPS-4856 (B-2): optional chaining .errors?.[0]?.message ile crash
        // riskini kapat — `errors` undefined ise `[0]` non-optional crash ediyordu.
        const raw = error?.response?.data?.errors?.[0]?.message;
        toast({
          title:
            raw ||
            t("common.unexpectedError", "Beklenmeyen bir hata oluştu."),
          variant: "error",
        });
        navigate("/extended-warranties/packages");
      }
    };
    loadPlan();
  }, [id]);

  // 3-level image upload/delete handlers — keyed by level. Mirrors the create
  // page; reuses ProductService endpoints since main-category and category
  // images are entities owned by the merchant, not by the plan itself.
  const handleImageUpload = async (level, event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    const MAX = 40 * 1024 * 1024;
    if (file.size > MAX) {
      toast({
        variant: "error",
        title: t("productsPage.fileSizeError", "Dosya boyutu çok büyük"),
      });
      return;
    }

    const mainId = currentPlan?.mainCategoryId;
    const catId = currentPlan?.categoryId;
    const productId = currentPlan?.merchantProductId;

    setImageBusy((b) => ({ ...b, [level]: true }));
    try {
      if (level === "main" && mainId) {
        const uploadRes = await ProductService.uploadFile(file);
        const url = uploadRes?.url || uploadRes?.data?.url;
        if (!url) throw new Error("upload-url-missing");
        await ProductService.updateMainCategoryImage(mainId, url);
        setMainCategoryImageUrl(url);
      } else if (level === "cat" && catId) {
        const uploadRes = await ProductService.uploadFile(file);
        const url = uploadRes?.url || uploadRes?.data?.url;
        if (!url) throw new Error("upload-url-missing");
        await ProductService.updateCategoryImage(catId, url);
        setCategoryImageUrl(url);
      } else if (level === "product" && productId) {
        const formData = new FormData();
        formData.append("file", file);
        const res = await ProductService.postProductImage(productId, formData);
        const url = res?.data?.imageUrl || res?.imageUrl || "";
        setProductImageUrl(url);
      }
      toast({
        title: t("productsPage.imageUploadSuccess", "Görsel güncellendi"),
        variant: "success",
      });
    } catch (err) {
      toast({
        variant: "error",
        title:
          err?.response?.data?.errors?.[0]?.message ||
          t("productsPage.imageUploadError", "Görsel yüklenemedi"),
      });
    } finally {
      setImageBusy((b) => ({ ...b, [level]: false }));
    }
  };

  const handleImageDelete = async (level) => {
    const mainId = currentPlan?.mainCategoryId;
    const catId = currentPlan?.categoryId;
    const productId = currentPlan?.merchantProductId;
    setImageBusy((b) => ({ ...b, [level]: true }));
    try {
      if (level === "main" && mainId) {
        await ProductService.deleteMainCategoryImage(mainId);
        setMainCategoryImageUrl("");
      } else if (level === "cat" && catId) {
        await ProductService.deleteCategoryImage(catId);
        setCategoryImageUrl("");
      } else if (level === "product" && productId) {
        await ProductService.deleteProductImage(productId);
        setProductImageUrl("");
      }
      toast({
        title: t("productsPage.imageDeleteSuccess", "Görsel silindi"),
        variant: "success",
      });
    } catch (err) {
      toast({
        variant: "error",
        title:
          err?.response?.data?.errors?.[0]?.message ||
          t("productsPage.imageDeleteError", "Görsel silinemedi"),
      });
    } finally {
      setImageBusy((b) => ({ ...b, [level]: false }));
    }
  };

  const handleSerialNoInfoSave = async () => {
    const catId = currentPlan?.categoryId;
    if (!catId) return;
    setSerialNoInfoBusy(true);
    try {
      await ProductService.updateCategorySerialNoInfo(
        catId,
        categorySerialNoInfo.trim() || null,
      );
      toast({
        title: t(
          "extendedWarranties.serialNoInfoSaved",
          "Seri no bilgisi güncellendi",
        ),
        variant: "success",
      });
    } catch (err) {
      toast({
        variant: "error",
        title:
          err?.response?.data?.errors?.[0]?.message ||
          t(
            "extendedWarranties.serialNoInfoSaveError",
            "Seri no bilgisi güncellenemedi",
          ),
      });
    } finally {
      setSerialNoInfoBusy(false);
    }
  };

  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result.split(",")[1]);
      reader.onerror = reject;
    });

  const handlePdfSelect = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const MAX_PDF = 10 * 1024 * 1024;
    if (file.size > MAX_PDF) {
      toast({
        variant: "error",
        title: t(
          "extendedWarranties.pdfSizeError",
          "PDF dosyası en fazla 10 MB olabilir",
        ),
      });
      return;
    }
    setContractPdfFile(file);
  };

  const transformFormDataToPayload = (formData) => {
    // EPS-4863: durationMonth string olarak gelir (zod regex doğal sayı garantili);
    // parseInt ile sayıya çevrilir. Boşsa mevcut plan değerine düş.
    // warrantyYear/planDuration ay sayısından türetilir (geriye uyum).
    const parsedDurationMonth = parseInt(formData.durationMonth, 10);
    const safeDurationMonth = Number.isNaN(parsedDurationMonth)
      ? (currentPlan?.durationMonth ??
        (currentPlan?.warrantyYear ?? currentPlan?.duration ?? 1) * 12)
      : parsedDurationMonth;
    const derivedWarrantyYear = Math.round(safeDurationMonth / 12);

    if (isBarem) {
      // BE MerchantAdditionalServicePlanRequest: planBarem wrapper'ı şart;
      // wrapper olmadan request.getPlanBarem() null döner ve converter NPE atar.

      // EPS-4855: BE PUT artık barems array kabul ediyor (PR #201).
      // Boş satırlar atlanır; en az 1 satır kalmalı (BE planPrice zorunlu).
      const baremsPayload = (formData.barems || [])
        .map((b) => ({
          minPrice: b.minPrice,
          maxPrice: b.maxPrice,
          planPrice: b.planPrice,
          packageCode: b.packageCode || null,
        }))
        .filter(
          (b) =>
            b.minPrice !== undefined ||
            b.maxPrice !== undefined ||
            b.planPrice !== undefined ||
            (b.packageCode && b.packageCode.length > 0),
        );

      const baremPlanDescPayload = (formData.planDescription || "").trim() || null;
      return {
        planType: "BAREM",
        planBarem: {
          name: formData.name,
          durationMonth: safeDurationMonth,
          warrantyYear: derivedWarrantyYear,
          status: formData.status,
          categoryId: currentPlan?.categoryId ?? null,
          mainCategoryId: currentPlan?.mainCategoryId ?? null,
          subCategoryId: currentPlan?.subCategoryId ?? null,
          subCategoryBrandId: currentPlan?.subCategoryBrandId ?? null,
          merchantProductId: currentPlan?.merchantProductId ?? null,
          packageCategory: formData.packageCategory ?? null,
          barems: baremsPayload,
          planDescription: baremPlanDescPayload,
        },
        // EPS-4971: tahsilat yöntemi plan tipinden bağımsız → istek kökünde.
        collectionTypes: formData.collectionTypes,
      };
    }
    const planDurationEnum =
      safeDurationMonth >= 36
        ? "THREE_YEAR"
        : safeDurationMonth >= 24
          ? "TWO_YEAR"
          : "ONE_YEAR";
    const basicPlanDescPayload = (formData.planDescription || "").trim() || null;
    // EPS-4970: Standart Planın oransal aralıkları. Her zaman array gönderilir —
    // BE'de null "dokunma", [] ise "tüm aralıkları temizle" anlamına geliyor;
    // kullanıcı son aralığı sildiğinde temizlik gerçekleşsin.
    const rateBaremsPayload = (formData.rateBarems || [])
      .map((b) => ({
        id: b.id,
        minPrice: b.minPrice,
        maxPrice: b.maxPrice,
        rate: b.rate,
        packageCode: b.packageCode || null,
      }))
      .filter(
        (b) =>
          b.minPrice !== undefined ||
          b.maxPrice !== undefined ||
          b.rate !== undefined ||
          (b.packageCode && b.packageCode.length > 0),
      );
    return {
      planBasics: {
        name: formData.name,
        durationMonth: safeDurationMonth,
        planDuration: planDurationEnum,
        status: formData.status,
        planDescription: basicPlanDescPayload,
        // EPS-4970: paket kategorisi düzenlenebilir; kategori bağı create anında
        // belirlendiği için (baremli plandaki gibi) mevcut id'ler aynen geri gönderilir.
        packageCategory: formData.packageCategory ?? null,
        mainCategoryId: currentPlan?.mainCategoryId ?? null,
        categoryId: currentPlan?.categoryId ?? null,
        subCategoryId: currentPlan?.subCategoryId ?? null,
        subCategoryBrandId: currentPlan?.subCategoryBrandId ?? null,
        merchantProductId: currentPlan?.merchantProductId ?? null,
        barems: rateBaremsPayload,
      },
      // EPS-4970 revize: planDetails gönderilmiyor. Alanlar artık formda yok;
      // BE partialUpdate null'ları atladığı için eski planların mevcut değerleri
      // (varsa) olduğu gibi kalır.
      planCollectionAndGivingBack: {
        givingBackPolicy: t("extendedWarranties.fourteenDays"),
      },
      // EPS-4971: tahsilat yöntemi plan tipinden bağımsız → istek kökünde.
      collectionTypes: formData.collectionTypes,
    };
  };

  const onSubmit = async (data) => {
    // Plan adı (her iki tip için de zorunlu)
    if (!data.name || data.name.trim().length < 2) {
      toast({
        title: t(
          "extendedWarranties.validateMsg.nameRequired",
          "Plan adı zorunludur",
        ),
        variant: "error",
      });
      return;
    }

    // EPS-4855 + EPS-4856: BAREM çoklu fiyat aralığı validation.
    // Kurallar: satır bütünlüğü, planPrice > 0, min < max, aralıklar disjoint.
    // newPackagePage ile aynı util'den geçer.
    if (isBarem) {
      // EPS-4930: Paket kategorisi zorunlu (Uzatılmış Garanti / Diğer).
      if (!data.packageCategory) {
        const msg = t(
          "extendedWarranties.validateMsg.packageCategoryRequired",
          "Paket kategorisi zorunludur",
        );
        form.setError("packageCategory", { type: "manual", message: msg });
        toast({ title: msg, variant: "error" });
        return;
      }
      const rows = Array.isArray(data.barems) ? data.barems : [];
      // Aralıklar baremli planın tek fiyat kaynağı. Satır sayısı kontrolü tek
      // başına yetmiyordu: tek satır bırakılıp alanları boşaltıldığında
      // validateBarems o satırı atlıyor ve plan boş barems[] ile kaydediliyordu.
      if (!hasFilledBarems(rows)) {
        const msg = t(
          "extendedWarranties.validateMsg.atLeastOneBarem",
          "En az bir fiyat aralığı girmelisiniz",
        );
        if (rows.length > 0) {
          form.setError("barems.0.minPrice", { type: "manual", message: msg });
        }
        toast({ title: msg, variant: "error" });
        return;
      }
      const baremErrors = validateBarems(rows, t);
      if (baremErrors.length > 0) {
        baremErrors.forEach((e) =>
          form.setError(e.path, { type: "manual", message: e.message }),
        );
        toast({ title: baremErrors[0].message, variant: "error" });
        return;
      }
    } else {
      // EPS-4970: Paket kategorisi zorunlu (baremli plandaki ile aynı kural).
      if (!data.packageCategory) {
        const msg = t(
          "extendedWarranties.validateMsg.packageCategoryRequired",
          "Paket kategorisi zorunludur",
        );
        form.setError("packageCategory", { type: "manual", message: msg });
        toast({ title: msg, variant: "error" });
        return;
      }
      // Oransal aralıklar — baremli plandaki kurallarla birebir, ek olarak
      // paket kodu zorunlu (plan seviyesinde kod yok).
      const rateBaremErrors = validateBarems(data.rateBarems, t, {
        priceField: "rate",
        fieldPrefix: "rateBarems",
        requirePackageCode: true,
      });
      if (rateBaremErrors.length > 0) {
        rateBaremErrors.forEach((e) =>
          form.setError(e.path, { type: "manual", message: e.message }),
        );
        toast({ title: rateBaremErrors[0].message, variant: "error" });
        return;
      }
      // EPS-4970 revize: Satış fiyatı alanı kaldırıldıktan sonra aralıklar planın
      // tek fiyat kaynağı — aralığı olan bir planın son aralığı silinerek
      // kaydedilemez, aksi hâlde plan hiçbir kanalda fiyatlandırılamaz hâle gelir.
      // Hiç aralığı olmayan ESKİ planlar bu kontrole girmez; yoksa adını
      // değiştirmek/pasife almak için aralık uydurmak zorunda kalınırdı.
      const planHadRateBarems = (currentPlan?.rateBarems || []).length > 0;
      if (planHadRateBarems && !hasFilledRateBarems(data.rateBarems)) {
        const msg = t(
          "extendedWarranties.validateMsg.atLeastOneRateBarem",
          "En az bir fiyat aralığı girmelisiniz",
        );
        toast({ title: msg, variant: "error" });
        return;
      }
    }

    try {
      const contractPdf = contractPdfFile
        ? {
            name: contractPdfFile.name,
            mimeType: contractPdfFile.type,
            content: await fileToBase64(contractPdfFile),
          }
        : null;

      const transformedData = {
        ...transformFormDataToPayload(data),
        contractPdf,
      };
      await updateAdditionalServicePlan(id, transformedData);

      toast({
        title: t("common.successfullyUpdated"),
        variant: "success",
      });
      navigate("/extended-warranties/packages");
    } catch (error) {
      // EPS-4856 (B-4): BE i18n key dönüyorsa Locize'dan çevir; yoksa raw'ı göster.
      const raw =
        error?.response?.data?.errors?.[0]?.message ||
        error?.response?.data?.message;
      toast({
        title: raw
          ? t(raw, raw)
          : t("common.unexpectedError", "Beklenmeyen bir hata oluştu."),
        variant: "error",
      });
    }
  };

  // BUG-5 fix: react-hook-form schema reddi sessiz kalmasın; ilk hatayı toast et.
  const onSubmitError = (errors) => {
    const firstField = Object.keys(errors || {})[0];
    const firstMsg =
      firstField &&
      (errors[firstField]?.message ||
        t(
          "extendedWarranties.validateMsg.formInvalid",
          "Lütfen formu kontrol edin",
        ));
    toast({
      title:
        firstMsg ||
        t(
          "extendedWarranties.validateMsg.formInvalid",
          "Lütfen formu kontrol edin",
        ),
      variant: "error",
    });
  };

  const handleDelete = async () => {
    try {
      await deleteAdditionalServicePlan(id);
      toast({
        title: t("common.successfullyDeleted"),
        variant: "success",
      });
      navigate("/extended-warranties/packages");
    } catch (error) {
      // EPS-4856 (B-4): BE i18n key dönüyorsa Locize'dan çevir; yoksa raw'ı göster.
      const raw =
        error?.response?.data?.errors?.[0]?.message ||
        error?.response?.data?.message;
      toast({
        title: raw
          ? t(raw, raw)
          : t("common.unexpectedError", "Beklenmeyen bir hata oluştu."),
        variant: "error",
      });
    }
  };

  // EPS-4970: Kategori bilgileri artık HER İKİ plan tipinde de gösteriliyor —
  // Standart Plan da baremli plan gibi kategori hiyerarşisine bağlanıyor.
  // Baremli plandaki davranışın aynısı: kategori create anında belirlenir,
  // burada salt-okunur gösterilir (görsel/seri-no yönetimi düzenlenebilir).
  const categorySection = (
              <div className="rounded-lg bg-white p-6 shadow-sm">
                <h2 className="mb-6 text-lg font-semibold">
                  {t(
                    "extendedWarrantiesPage.baremCategoryInfo",
                    "Kategori Bilgileri",
                  )}
                </h2>
                <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                  <div className="flex flex-col gap-3">
                    <div className="flex flex-col gap-2">
                      <Label>
                        {t("productsPage.mainCategory", "Ana Kategori")}
                      </Label>
                      <Input
                        readOnly
                        className="bg-gray-50"
                        value={
                          mainCategoryName ||
                          (currentPlan?.mainCategoryId
                            ? `#${currentPlan.mainCategoryId}`
                            : "-")
                        }
                      />
                    </div>
                    {currentPlan?.mainCategoryId ? (
                      <ImageBlock
                        t={t}
                        levelLabel={t(
                          "productsPage.mainCategory",
                          "Ana Kategori",
                        )}
                        url={mainCategoryImageUrl}
                        busyFlag={imageBusy.main}
                        inputRef={mainImgRef}
                        onUpload={(e) => handleImageUpload("main", e)}
                        onDelete={() => handleImageDelete("main")}
                      />
                    ) : null}
                  </div>
                  <div className="flex flex-col gap-3">
                    <div className="flex flex-col gap-2">
                      <Label>{t("productsPage.category", "Kategori")}</Label>
                      <Input
                        readOnly
                        className="bg-gray-50"
                        value={
                          categoryName ||
                          (currentPlan?.categoryId
                            ? `#${currentPlan.categoryId}`
                            : "-")
                        }
                      />
                    </div>
                    {currentPlan?.categoryId ? (
                      <ImageBlock
                        t={t}
                        levelLabel={t(
                          "productsPage.subCategory",
                          "Alt Kategori",
                        )}
                        url={categoryImageUrl}
                        busyFlag={imageBusy.cat}
                        inputRef={catImgRef}
                        onUpload={(e) => handleImageUpload("cat", e)}
                        onDelete={() => handleImageDelete("cat")}
                      />
                    ) : null}
                  </div>
                  {/* EPS-4873: Alt kategori (read-only). */}
                  <div className="flex flex-col gap-3">
                    <div className="flex flex-col gap-2">
                      <Label>
                        {t("productsPage.subCategory", "Alt Kategori")}
                      </Label>
                      <Input
                        readOnly
                        className="bg-gray-50"
                        value={
                          subCategoryName ||
                          (currentPlan?.subCategoryId
                            ? `#${currentPlan.subCategoryId}`
                            : "-")
                        }
                      />
                    </div>
                  </div>
                  {/* EPS-4873: Marka (read-only). */}
                  <div className="flex flex-col gap-3">
                    <div className="flex flex-col gap-2">
                      <Label>{t("productsPage.brand", "Marka")}</Label>
                      <Input
                        readOnly
                        className="bg-gray-50"
                        value={
                          subCategoryBrandName ||
                          (currentPlan?.subCategoryBrandId
                            ? `#${currentPlan.subCategoryBrandId}`
                            : "-")
                        }
                      />
                    </div>
                  </div>
                  <div className="flex flex-col gap-3">
                    <div className="flex flex-col gap-2">
                      <Label>
                        {t(
                          "extendedWarranties.merchantProductOptional",
                          "Ürün (Opsiyonel)",
                        )}
                      </Label>
                      <Input
                        readOnly
                        className="bg-gray-50"
                        value={
                          merchantProductName ||
                          (currentPlan?.merchantProductId
                            ? `#${currentPlan.merchantProductId}`
                            : t(
                                "extendedWarranties.allCategoryProducts",
                                "Tüm kategori için",
                              ))
                        }
                      />
                    </div>
                    {currentPlan?.merchantProductId ? (
                      <ImageBlock
                        t={t}
                        levelLabel={t(
                          "productsPage.productImage",
                          "Ürün Görseli",
                        )}
                        url={productImageUrl}
                        busyFlag={imageBusy.product}
                        inputRef={productImgRef}
                        onUpload={(e) => handleImageUpload("product", e)}
                        onDelete={() => handleImageDelete("product")}
                      />
                    ) : null}
                  </div>
                </div>

                {/* EPS-4850: Kategori seri no bilgisi — embed-app i ikonu tooltip metni */}
                {currentPlan?.categoryId ? (
                  <div className="mt-6 flex flex-col gap-2">
                    <Label>
                      {t(
                        "extendedWarranties.categorySerialNoInfo",
                        "Seri No Bilgisi",
                      )}
                    </Label>
                    <Textarea
                      rows={3}
                      maxLength={500}
                      placeholder={t(
                        "extendedWarranties.categorySerialNoInfoPlaceholder",
                        "Müşterilere seri numarasını nerede bulacaklarını açıklayın (örn: ürünün arka etiketinde 15 haneli numara)",
                      )}
                      value={categorySerialNoInfo}
                      onChange={(e) => setCategorySerialNoInfo(e.target.value)}
                    />
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-gray-500">
                        {t(
                          "extendedWarranties.categorySerialNoInfoHint",
                          "embed.egaranti.com'da seri no alanının yanındaki \"i\" ikonunda gösterilir.",
                        )}
                      </p>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={serialNoInfoBusy}
                        onClick={handleSerialNoInfoSave}
                      >
                        {serialNoInfoBusy
                          ? t("common.saving", "Kaydediliyor...")
                          : t("common.save", "Kaydet")}
                      </Button>
                    </div>
                  </div>
                ) : null}
              </div>
  );

  if (loading || !currentPlan) {
    return <div>Loading...</div>;
  }

  return (
    <main className="flex min-h-screen flex-col bg-[#F9FAFC]">
      <Breadcrumb>
        <BreadcrumbItem link="/extended-warranties/packages">
          {t("navbar.menuItems.packages")}
        </BreadcrumbItem>
        <BreadcrumbItem active>
          {t("extendedWarrantiesPage.detail")} - {currentPlan.name}
        </BreadcrumbItem>
      </Breadcrumb>
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6 flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-semibold text-[#111729]">
            {currentPlan.name}
          </h1>
          <span
            className={`rounded-full px-3 py-1 text-xs font-medium ${
              isBarem
                ? "bg-purple-100 text-purple-700"
                : "bg-blue-100 text-blue-700"
            }`}
          >
            {isBarem
              ? t("extendedWarranties.planTypeBarem", "Baremli Plan")
              : t("extendedWarranties.planTypeBasic", "Standart Plan")}
          </span>
          <span
            className={`rounded-full px-3 py-1 text-xs font-medium ${
              currentPlan.status === "ACTIVE"
                ? "bg-green-100 text-green-700"
                : "bg-gray-100 text-gray-700"
            }`}
          >
            {currentPlan.status === "ACTIVE"
              ? t("extendedWarranties.active", "Aktif")
              : t("extendedWarranties.passive", "Pasif")}
          </span>
        </div>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit, onSubmitError)}
            className="space-y-6"
          >
            {/* EPS-4970: Eskiden "hidden" ile mount kalıyordu; kategori bölümü artık
               iki plan tipinde de kullanıldığı için koşullu render'a çevrildi —
               aksi hâlde BAREM planı açıkken categorySection iki kez mount olup
               duplike DOM id ve çift görsel yükleme alanı üretiyordu. isBarem
               plana göre sabit, sekme değişimi yok. */}
            {!isBarem && (
            <div className="space-y-6">
            <div className="rounded-lg bg-white p-6 shadow-sm">
              <h2 className="mb-6 text-lg font-semibold">
                {t("extendedWarrantiesPage.packageBasicInfo")}
              </h2>

              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <Label>{t("extendedWarranties.name")}</Label>
                      <FormControl>
                        <Input maxLength={50} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="planType"
                  render={() => (
                    <FormItem>
                      <Label>{t("extendedWarranties.planType")}</Label>
                      <FormControl>
                        <Input
                          value={
                            currentPlan.planType === "BAREM"
                              ? t("extendedWarranties.planTypeBarem", "Baremli Plan")
                              : t("extendedWarranties.planTypeBasic", "Standart Plan")
                          }
                          readOnly
                          className="bg-gray-50"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="durationMonth"
                  render={({ field }) => (
                    <FormItem>
                      <Label>{t("extendedWarranties.durationMonth", "Süre (Ay)")}</Label>
                      <FormControl>
                        <Input
                          type="text"
                          inputMode="numeric"
                          maxLength={2}
                          {...field}
                          value={field.value ?? ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* EPS-4970: Paket kategorisi — baremli plandaki ile aynı alan ve
                   aynı semantik (OTHER paketler UZG iş kurallarının dışında kalır). */}
                <FormField
                  control={form.control}
                  name="packageCategory"
                  render={({ field }) => (
                    <FormItem className="col-span-2">
                      <Label>
                        {t(
                          "extendedWarranties.packageCategory",
                          "Paket Kategorisi",
                        )}
                        <span className="ml-0.5 text-red-500">*</span>
                      </Label>
                      <RadioGroup
                        onValueChange={field.onChange}
                        value={field.value ?? ""}
                        className="flex items-center gap-2"
                      >
                        <FormItem className="flex items-center space-x-3">
                          <FormControl>
                            <RadioGroupItem
                              value="EXTENDED_WARRANTY"
                              id="basic-package-category-ew"
                            />
                          </FormControl>
                          <Label
                            htmlFor="basic-package-category-ew"
                            className="!mt-0 cursor-pointer"
                          >
                            {t(
                              "extendedWarranties.packageCategoryExtendedWarranty",
                              "Uzatılmış Garanti",
                            )}
                          </Label>
                        </FormItem>
                        <FormItem className="flex items-center space-x-3">
                          <FormControl>
                            <RadioGroupItem
                              value="OTHER"
                              id="basic-package-category-other"
                            />
                          </FormControl>
                          <Label
                            htmlFor="basic-package-category-other"
                            className="!mt-0 cursor-pointer"
                          >
                            {t("extendedWarranties.packageCategoryOther", "Diğer")}
                          </Label>
                        </FormItem>
                      </RadioGroup>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem className="col-span-2">
                      <Label>{t("extendedWarranties.status")}</Label>
                      <FormControl>
                        <RadioGroup
                          className="flex py-5"
                          onValueChange={(value) => {
                            form.setValue("status", value);
                          }}
                          value={form.watch("status")}
                          {...field}
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="ACTIVE" id="active-status" />
                            <Label
                              htmlFor="active-status"
                              className="!mt-0 cursor-pointer"
                            >
                              {t("extendedWarranties.active")}
                            </Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem
                              value="PASSIVE"
                              id="passive-status"
                            />
                            <Label
                              htmlFor="passive-status"
                              className="!mt-0 cursor-pointer"
                            >
                              {t("extendedWarranties.passive")}
                            </Label>
                          </div>
                        </RadioGroup>
                      </FormControl>
                    </FormItem>
                  )}
                />

                {/* EPS-4971: Tahsilat yöntemi — plan oluşturmadaki alanın aynısı,
                   satış sonrası ödeme akışını belirler. */}
                <CollectionTypesField control={form.control} />
              </div>

              {/* EPS-5860: Plan açıklaması — embed-app AddonCard'da gösterilir */}
              <div className="mt-6">
                <FormField
                  control={form.control}
                  name="planDescription"
                  render={({ field }) => (
                    <FormItem>
                      <Label>
                        {t(
                          "extendedWarranties.planDescription",
                          "Plan Açıklaması",
                        )}
                      </Label>
                      <FormControl>
                        <Textarea
                          rows={4}
                          maxLength={2000}
                          placeholder={t(
                            "extendedWarranties.planDescriptionPlaceholder",
                            "Bu paket ile son tüketicinin elde edeceği avantajları açıklayın (kapsam, onarım, değişim, muafiyet vb.)",
                          )}
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>
                        {t(
                          "extendedWarranties.planDescriptionHint",
                          "embed.egaranti.com'da paket kartı altında müşteriye gösterilir. Boş bırakırsanız standart bir metin eklenir.",
                        )}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* EPS-4970: Kategori bilgileri — baremli plandaki bölümün aynısı. */}
            {categorySection}

            {/* EPS-4970: Standart Planın oransal fiyat aralıkları — baremli plandaki
               aralık yapısının aynısı, aralığa düz fiyat yerine oran giriliyor.
               Paket fiyatı satışta ürünün kendi bedeli üzerinden hesaplanır. */}
            <div className="rounded-lg bg-white p-6 shadow-sm">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-lg font-semibold">
                  {t(
                    "extendedWarrantiesPage.rateBaremPriceInfo",
                    "Fiyat Aralıkları (Oransal)",
                  )}
                </h2>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    rateBaremsArray.append({
                      minPrice: undefined,
                      maxPrice: undefined,
                      rate: undefined,
                      packageCode: "",
                    })
                  }
                >
                  <Plus className="mr-1 h-4 w-4" />
                  {t("extendedWarranties.addBaremRow", "Aralık ekle")}
                </Button>
              </div>

              {rateBaremsArray.fields.length === 0 ? (
                <p className="rounded-md border border-dashed border-gray-300 px-4 py-6 text-center text-sm text-gray-500">
                  {t(
                    "extendedWarranties.rateBaremsRequiredEmpty",
                    "Aralık tanımlanmadı. Paket fiyatı aralıklardan hesaplandığı için en az bir aralık girmelisiniz.",
                  )}
                </p>
              ) : (
                <div className="space-y-4">
                  {rateBaremsArray.fields.map((field, index) => (
                    <div
                      key={field.id}
                      className="rounded-md border border-gray-200 p-4"
                    >
                      <div className="mb-2 flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-600">
                          {t("extendedWarranties.baremRow", "Aralık {{n}}", {
                            n: index + 1,
                          })}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => rateBaremsArray.remove(index)}
                          className="text-red-500 hover:bg-red-50 hover:text-red-600"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>

                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                        <FormField
                          control={form.control}
                          name={`rateBarems.${index}.minPrice`}
                          render={({ field }) => (
                            <FormItem>
                              <Label>
                                {t(
                                  "extendedWarranties.minPrice",
                                  "Minimum Fiyat",
                                )}
                              </Label>
                              <FormControl>
                                <InputWithIcon
                                  type="number"
                                  icon={
                                    <span className="text-[#111729]">₺</span>
                                  }
                                  {...field}
                                  value={field.value ?? ""}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    field.onChange(
                                      v === "" ? undefined : Number(v),
                                    );
                                  }}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name={`rateBarems.${index}.maxPrice`}
                          render={({ field }) => (
                            <FormItem>
                              <Label>
                                {t(
                                  "extendedWarranties.maxPrice",
                                  "Maksimum Fiyat",
                                )}
                              </Label>
                              <FormControl>
                                <InputWithIcon
                                  type="number"
                                  icon={
                                    <span className="text-[#111729]">₺</span>
                                  }
                                  {...field}
                                  value={field.value ?? ""}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    field.onChange(
                                      v === "" ? undefined : Number(v),
                                    );
                                  }}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name={`rateBarems.${index}.rate`}
                          render={({ field }) => (
                            <FormItem>
                              <Label>
                                {t("extendedWarranties.rate", "Oran")}
                              </Label>
                              <FormControl>
                                <InputWithIcon
                                  type="number"
                                  icon={
                                    <span className="text-[#111729]">%</span>
                                  }
                                  {...field}
                                  value={field.value ?? ""}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    field.onChange(
                                      v === "" ? undefined : Number(v),
                                    );
                                  }}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name={`rateBarems.${index}.packageCode`}
                          render={({ field }) => (
                            <FormItem>
                              <Label>
                                {t(
                                  "extendedWarranties.packageCode",
                                  "Paket Kodu",
                                )}
                                <span className="ml-0.5 text-red-500">*</span>
                              </Label>
                              <FormControl>
                                <Input maxLength={64} {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <FormDescription className="mt-3">
                {t(
                  "extendedWarranties.rateBaremRowsHint",
                  "Her aralık için minimum-maksimum ürün fiyatı, uygulanacak oran ve paket kodunu girin. Paket fiyatı, ürünün kendi bedeli üzerinden hesaplanır (ürün fiyatı × oran). Paket kodu sigorta kanallarına gönderilen isteklerde kullanılır.",
                )}
              </FormDescription>
            </div>
            </div>
            )}

            {isBarem && (
              <div className="space-y-6">
                <div className="rounded-lg bg-white p-6 shadow-sm">
                  <h2 className="mb-6 text-lg font-semibold">
                    {t(
                      "extendedWarrantiesPage.packageBasicInfo",
                      "Plan Bilgileri",
                    )}
                  </h2>
                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <Label>{t("extendedWarranties.name")}</Label>
                          <FormControl>
                            <Input maxLength={50} {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="durationMonth"
                      render={({ field }) => (
                        <FormItem>
                          <Label>{t("extendedWarranties.durationMonth", "Süre (Ay)")}</Label>
                          <FormControl>
                            <Input
                              type="text"
                              inputMode="numeric"
                              maxLength={2}
                              {...field}
                              value={field.value ?? ""}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* EPS-4930: Paket kategorisi — Uzatılmış Garanti / Diğer.
                       BE'de package_category kolonuna yazılır. */}
                    <FormField
                      control={form.control}
                      name="packageCategory"
                      render={({ field }) => (
                        <FormItem className="col-span-2">
                          <Label>
                            {t(
                              "extendedWarranties.packageCategory",
                              "Paket Kategorisi",
                            )}
                            <span className="ml-0.5 text-red-500">*</span>
                          </Label>
                          <RadioGroup
                            onValueChange={field.onChange}
                            value={field.value ?? ""}
                            className="flex items-center gap-2"
                          >
                            <FormItem className="flex items-center space-x-3">
                              <FormControl>
                                <RadioGroupItem
                                  value="EXTENDED_WARRANTY"
                                  id="barem-package-category-ew-detail"
                                />
                              </FormControl>
                              <Label
                                htmlFor="barem-package-category-ew-detail"
                                className="!mt-0 cursor-pointer"
                              >
                                {t(
                                  "extendedWarranties.packageCategoryExtendedWarranty",
                                  "Uzatılmış Garanti",
                                )}
                              </Label>
                            </FormItem>
                            <FormItem className="flex items-center space-x-3">
                              <FormControl>
                                <RadioGroupItem
                                  value="OTHER"
                                  id="barem-package-category-other-detail"
                                />
                              </FormControl>
                              <Label
                                htmlFor="barem-package-category-other-detail"
                                className="!mt-0 cursor-pointer"
                              >
                                {t(
                                  "extendedWarranties.packageCategoryOther",
                                  "Diğer",
                                )}
                              </Label>
                            </FormItem>
                          </RadioGroup>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="status"
                      render={({ field }) => (
                        <FormItem className="col-span-2">
                          <Label>{t("extendedWarranties.status")}</Label>
                          <RadioGroup
                            onValueChange={field.onChange}
                            value={field.value}
                            className="flex items-center gap-2"
                          >
                            <FormItem className="flex items-center space-x-3">
                              <FormControl>
                                <RadioGroupItem
                                  value="ACTIVE"
                                  id="barem-active-status-detail"
                                />
                              </FormControl>
                              <Label
                                htmlFor="barem-active-status-detail"
                                className="!mt-0 cursor-pointer"
                              >
                                {t("extendedWarranties.active")}
                              </Label>
                            </FormItem>
                            <FormItem className="flex items-center space-x-3">
                              <FormControl>
                                <RadioGroupItem
                                  value="PASSIVE"
                                  id="barem-passive-status-detail"
                                />
                              </FormControl>
                              <Label
                                htmlFor="barem-passive-status-detail"
                                className="!mt-0 cursor-pointer"
                              >
                                {t("extendedWarranties.passive")}
                              </Label>
                            </FormItem>
                          </RadioGroup>
                        </FormItem>
                      )}
                    />

                    {/* EPS-4971: Baremli planda da tahsilat yöntemi seçilir —
                       yöntem plan seviyesinde, aralık seviyesinde değil. */}
                    <CollectionTypesField control={form.control} />
                  </div>

                  {/* EPS-5860: Plan açıklaması — embed-app AddonCard'da gösterilir */}
                  <div className="mt-6">
                    <FormField
                      control={form.control}
                      name="planDescription"
                      render={({ field }) => (
                        <FormItem>
                          <Label>
                            {t(
                              "extendedWarranties.planDescription",
                              "Plan Açıklaması",
                            )}
                          </Label>
                          <FormControl>
                            <Textarea
                              rows={4}
                              maxLength={2000}
                              placeholder={t(
                                "extendedWarranties.planDescriptionPlaceholder",
                                "Bu paket ile son tüketicinin elde edeceği avantajları açıklayın (kapsam, onarım, değişim, muafiyet vb.)",
                              )}
                              {...field}
                            />
                          </FormControl>
                          <FormDescription>
                            {t(
                              "extendedWarranties.planDescriptionHint",
                              "embed.egaranti.com'da paket kartı altında müşteriye gösterilir. Boş bırakırsanız standart bir metin eklenir.",
                            )}
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                {categorySection}

                {/* EPS-4855: Çoklu fiyat aralığı + paket kodu. Her satır kendi
                   min/max/plan/packageCode'una sahip. Plus ile satır eklenir,
                   çöp ikonu ile silinir. En az 1 satır zorunlu. */}
                <div className="rounded-lg bg-white p-6 shadow-sm">
                  <div className="mb-4 flex items-center justify-between">
                    <h2 className="text-lg font-semibold">
                      {t(
                        "extendedWarrantiesPage.baremPriceInfo",
                        "Fiyat Aralıkları",
                      )}
                    </h2>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        baremsArray.append({
                          minPrice: undefined,
                          maxPrice: undefined,
                          planPrice: undefined,
                          packageCode: "",
                        })
                      }
                    >
                      <Plus className="mr-1 h-4 w-4" />
                      {t("extendedWarranties.addBaremRow", "Aralık ekle")}
                    </Button>
                  </div>

                  <div className="space-y-4">
                    {baremsArray.fields.map((field, index) => (
                      <div
                        key={field.id}
                        className="rounded-md border border-gray-200 p-4"
                      >
                        <div className="mb-2 flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-600">
                            {t("extendedWarranties.baremRow", "Aralık {{n}}", {
                              n: index + 1,
                            })}
                          </span>
                          {baremsArray.fields.length > 1 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => baremsArray.remove(index)}
                              className="text-red-500 hover:bg-red-50 hover:text-red-600"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>

                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                          <FormField
                            control={form.control}
                            name={`barems.${index}.minPrice`}
                            render={({ field }) => (
                              <FormItem>
                                <Label>
                                  {t(
                                    "extendedWarranties.minPrice",
                                    "Minimum Fiyat",
                                  )}
                                </Label>
                                <FormControl>
                                  <InputWithIcon
                                    type="number"
                                    icon={
                                      <span className="text-[#111729]">₺</span>
                                    }
                                    {...field}
                                    value={field.value ?? ""}
                                    onChange={(e) => {
                                      const v = e.target.value;
                                      field.onChange(
                                        v === "" ? undefined : Number(v),
                                      );
                                    }}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name={`barems.${index}.maxPrice`}
                            render={({ field }) => (
                              <FormItem>
                                <Label>
                                  {t(
                                    "extendedWarranties.maxPrice",
                                    "Maksimum Fiyat",
                                  )}
                                </Label>
                                <FormControl>
                                  <InputWithIcon
                                    type="number"
                                    icon={
                                      <span className="text-[#111729]">₺</span>
                                    }
                                    {...field}
                                    value={field.value ?? ""}
                                    onChange={(e) => {
                                      const v = e.target.value;
                                      field.onChange(
                                        v === "" ? undefined : Number(v),
                                      );
                                    }}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name={`barems.${index}.planPrice`}
                            render={({ field }) => (
                              <FormItem>
                                <Label>
                                  {t(
                                    "extendedWarranties.planPrice",
                                    "Plan Fiyatı",
                                  )}
                                </Label>
                                <FormControl>
                                  <InputWithIcon
                                    type="number"
                                    icon={
                                      <span className="text-[#111729]">₺</span>
                                    }
                                    {...field}
                                    value={field.value ?? ""}
                                    onChange={(e) => {
                                      const v = e.target.value;
                                      field.onChange(
                                        v === "" ? undefined : Number(v),
                                      );
                                    }}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name={`barems.${index}.packageCode`}
                            render={({ field }) => (
                              <FormItem>
                                <Label>
                                  {t(
                                    "extendedWarranties.packageCode",
                                    "Paket Kodu",
                                  )}
                                </Label>
                                <FormControl>
                                  <Input maxLength={64} {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                  <FormDescription className="mt-3">
                    {t(
                      "extendedWarranties.baremRowsHint",
                      "Her aralık için minimum-maksimum ürün fiyatı, plan fiyatı ve opsiyonel paket kodu girin.",
                    )}
                  </FormDescription>
                </div>
              </div>
            )}

            <div className="rounded-lg bg-white p-6 shadow-sm">
              <h2 className="mb-4 text-lg font-semibold">
                {t(
                  "extendedWarranties.contractPdf",
                  "Sözleşme PDF",
                )}
              </h2>
              <p className="mb-4 text-sm text-gray-500">
                {t(
                  "extendedWarranties.contractPdfDescription",
                  "Bu pakete ait sözleşme PDF dosyasını yükleyebilirsiniz (opsiyonel, maks. 10 MB).",
                )}
              </p>
              <input
                ref={pdfInputRef}
                type="file"
                accept="application/pdf"
                className="hidden"
                onChange={handlePdfSelect}
              />
              {contractPdfFile ? (
                <div className="flex items-center gap-3 rounded-md border border-gray-200 bg-gray-50 px-4 py-3">
                  <FileText className="h-5 w-5 shrink-0 text-red-500" />
                  <span className="min-w-0 flex-1 truncate text-sm font-medium text-gray-700">
                    {contractPdfFile.name}
                  </span>
                  <span className="shrink-0 text-xs text-gray-400">
                    {(contractPdfFile.size / 1024 / 1024).toFixed(2)} MB
                  </span>
                  <button
                    type="button"
                    onClick={() => setContractPdfFile(null)}
                    className="shrink-0 rounded p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : currentPlan?.contractPdfUrl ? (
                <div className="flex items-center gap-3 rounded-md border border-gray-200 bg-gray-50 px-4 py-3">
                  <FileText className="h-5 w-5 shrink-0 text-red-500" />
                  <span className="min-w-0 flex-1 truncate text-sm font-medium text-gray-700">
                    {t(
                      "extendedWarranties.pdfUploaded",
                      "Sözleşme PDF yüklü",
                    )}
                  </span>
                  <a
                    href={currentPlan.contractPdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="shrink-0 text-sm font-medium text-blue-600 hover:underline"
                  >
                    {t("extendedWarranties.viewPdf", "Görüntüle")}
                  </a>
                  <button
                    type="button"
                    onClick={() => pdfInputRef.current?.click()}
                    className="shrink-0 text-sm font-medium text-gray-500 hover:text-gray-700 hover:underline"
                  >
                    {t("extendedWarranties.replacePdf", "Değiştir")}
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => pdfInputRef.current?.click()}
                  className="flex w-full items-center justify-center gap-2 rounded-md border-2 border-dashed border-gray-300 px-4 py-6 text-sm text-gray-500 transition-colors hover:border-gray-400 hover:text-gray-600"
                >
                  <Upload className="h-5 w-5" />
                  {t(
                    "extendedWarranties.selectPdf",
                    "PDF dosyası seçin",
                  )}
                </button>
              )}
            </div>

            <div className="flex justify-end gap-8">
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button className="font-medium text-red-500 hover:text-red-600 hover:underline">
                    {t("common.delete")}
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent className="gap-0 px-4">
                  <div className="flex w-full flex-col items-center justify-center">
                    <span className="my-8 mb-1 text-center text-xl font-semibold text-[#111729]">
                      {t("extendedWarrantiesPage.deletePackage")}
                    </span>
                  </div>
                  <AlertDialogFooter className="m-0 mt-6">
                    <AlertDialogCancel className="w-full">
                      {t("payment.myCards.deleteCardDialog.btnTextCancel")}
                    </AlertDialogCancel>
                    <AlertDialogAction asChild>
                      <Button className="w-full" onClick={handleDelete}>
                        {t("payment.myCards.deleteCardDialog.btnTextConfirm")}
                      </Button>
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              <Button size="lg" type="submit">
                {t("common.save")}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </main>
  );
};

export default PackageDetail;
